import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TASK_TYPE_LABEL,
  SOURCE_ARTEFACT_LABEL,
  getTaskDestination,
  isTaskOverdue,
  formatTaskDue,
  taskMetaLine,
} from '../lib/taskNavigation';

const TASK_TYPE_FILTERS = ['all', 'approval', 'work', 'assignment', 'review'];
const SOURCE_FILTERS = ['all', 'disposition', 'requirement', 'build_item'];

export default function TaskInboxPanel({ tasks, loading, onNavigate }) {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  const visibleTasks = useMemo(() => tasks.filter((task) => {
    if (showOverdueOnly && !isTaskOverdue(task)) return false;
    if (typeFilter !== 'all' && task.task_type !== typeFilter) return false;
    if (sourceFilter !== 'all' && task.source_artefact_type !== sourceFilter) return false;
    return true;
  }), [tasks, typeFilter, sourceFilter, showOverdueOnly]);

  function handleOpen(task) {
    const dest = getTaskDestination(task);
    if (!dest) return;
    onNavigate?.();
    navigate({ pathname: dest.pathname, search: dest.search });
  }

  return (
    <div className="task-inbox">
      <div className="task-inbox-hdr">
        <span className="task-inbox-title">Your tasks</span>
        {!loading && tasks.length > 0 && (
          <span className="task-inbox-count">{tasks.length} open</span>
        )}
      </div>

      {tasks.length > 0 && (
        <div className="task-inbox-filters">
          <select
            className="task-inbox-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by task type"
          >
            {TASK_TYPE_FILTERS.map((key) => (
              <option key={key} value={key}>
                {key === 'all' ? 'All types' : TASK_TYPE_LABEL[key] || key}
              </option>
            ))}
          </select>
          <select
            className="task-inbox-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            aria-label="Filter by source"
          >
            {SOURCE_FILTERS.map((key) => (
              <option key={key} value={key}>
                {key === 'all' ? 'All sources' : SOURCE_ARTEFACT_LABEL[key] || key}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`task-inbox-pill ${showOverdueOnly ? 'on' : ''}`}
            onClick={() => setShowOverdueOnly((v) => !v)}
          >
            Overdue
          </button>
        </div>
      )}

      <div className="task-inbox-list">
        {loading && (
          <div className="task-inbox-empty">Loading tasks…</div>
        )}
        {!loading && !tasks.length && (
          <div className="task-inbox-empty">No open tasks assigned to you.</div>
        )}
        {!loading && tasks.length > 0 && !visibleTasks.length && (
          <div className="task-inbox-empty">No tasks match these filters.</div>
        )}
        {!loading && visibleTasks.map((task) => {
          const overdue = isTaskOverdue(task);
          const due = formatTaskDue(task.due_date);
          const dest = getTaskDestination(task);

          return (
            <button
              key={task.id}
              type="button"
              className={`task-inbox-item${overdue ? ' overdue' : ''}${!dest ? ' disabled' : ''}`}
              disabled={!dest}
              onClick={() => handleOpen(task)}
            >
              <div className="task-inbox-item-title">{task.title}</div>
              <div className="task-inbox-item-meta">{taskMetaLine(task)}</div>
              {due && (
                <div className={`task-inbox-item-due${overdue ? ' overdue' : ''}`}>
                  Due {due}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
